import { useState, useCallback } from 'react';
import api from '../services/api';

/**
 * Custom hook for managing message annotations
 * Handles all annotation-related state and operations
 */
export const useAnnotationManagement = (conversationState, token) => {
  // Annotation state
  const [annotatingMessageId, setAnnotatingMessageId] = useState(null);
  const [annotationInput, setAnnotationInput] = useState('');
  const [annotationTags, setAnnotationTags] = useState('');
  const [annotationError, setAnnotationError] = useState('');
  const [annotationLoading, setAnnotationLoading] = useState(false);

  // Start annotation handler
  const handleStartAnnotation = useCallback((messageId) => {
    setAnnotatingMessageId(messageId);
    setAnnotationInput('');
    setAnnotationTags('');
    setAnnotationError('');
  }, []);

  // Cancel annotation handler
  const handleCancelAnnotation = useCallback(() => {
    setAnnotatingMessageId(null);
    setAnnotationInput('');
    setAnnotationTags('');
    setAnnotationError('');
  }, []);

  // Save annotation handler
  const handleSaveAnnotation = useCallback(async (messageId) => {
    if (!messageId) {
      setAnnotationError('Message ID is required');
      return;
    }
    
    if (!annotationInput || !annotationInput.trim()) {
      setAnnotationError('Annotation text is required');
      return;
    }
    
    setAnnotationLoading(true);
    try {
      const annotationData = {
        text: annotationInput.trim(),
        tags: annotationTags ? annotationTags.split(',').map(tag => tag.trim()).filter(tag => tag) : []
      };
      
      console.log('DEBUG - About to send annotation:');
      console.log('- conversationId:', conversationState.selectedConversation._id);
      console.log('- messageId:', messageId);
      console.log('- annotationData:', annotationData);
      console.log('- token exists:', !!token);
      
      const response = await api.addAnnotation(conversationState.selectedConversation._id, messageId, annotationData, token);
      handleCancelAnnotation();
      
      // Update the message locally with the new annotation
      conversationState.setSelectedConversation(prevConvo => {
        if (!prevConvo || !prevConvo.messages) return prevConvo;
        
        const updatedMessages = prevConvo.messages.map(msg => {
          if (msg._id === messageId) {
            const newAnnotation = {
              _id: response.annotation?._id || Date.now().toString(),
              text: annotationData.text,
              tags: annotationData.tags || [],
              timestamp: new Date().toISOString(),
              therapistId: 'current-therapist'
            };
            
            return {
              ...msg,
              annotations: msg.annotations ? [...msg.annotations, newAnnotation] : [newAnnotation]
            };
          }
          return msg;
        });
        
        return { ...prevConvo, messages: updatedMessages };
      });
      
      return 'Annotation added successfully';
    } catch (err) {
      console.error('Error adding annotation:', err);
      setAnnotationError(err.message || 'Failed to add annotation');
      throw err;
    } finally {
      setAnnotationLoading(false);
    }
  }, [annotationInput, annotationTags, token, handleCancelAnnotation, conversationState]);

  // Edit annotation handler
  const handleEditAnnotation = useCallback(async (messageId, annotationId, updatedData) => {
    if (!conversationState.selectedConversation) return;
    
    setAnnotationLoading(true);
    try {
      try {
        // Try API call first (when backend supports it)
        await api.editAnnotation(conversationState.selectedConversation._id, messageId, annotationId, updatedData, token);
      } catch (apiError) {
        console.log('API edit not available, updating locally:', apiError.message);
      }
      
      // Update locally regardless (for immediate UI feedback)
      conversationState.setSelectedConversation(prevConvo => {
        if (!prevConvo || !prevConvo.messages) return prevConvo;
        
        const updatedMessages = prevConvo.messages.map(msg => {
          if (msg._id === messageId && msg.annotations) {
            const updatedAnnotations = msg.annotations.map(ann => {
              if (ann._id === annotationId) {
                return {
                  ...ann,
                  text: updatedData.text,
                  tags: updatedData.tags,
                  editedAt: new Date().toISOString(),
                  originalTimestamp: ann.timestamp
                };
              }
              return ann;
            });
            return { ...msg, annotations: updatedAnnotations };
          }
          return msg;
        });
        
        return { ...prevConvo, messages: updatedMessages };
      });
      
      return 'Annotation updated successfully';
    } catch (err) {
      console.error('Error editing annotation:', err);
      setAnnotationError(err.message || 'Failed to edit annotation');
      throw err;
    } finally {
      setAnnotationLoading(false);
    }
  }, [conversationState, token]);

  // Delete annotation handler
  const handleDeleteAnnotation = useCallback(async (messageId, annotationId) => {
    if (!conversationState.selectedConversation) return;
    
    setAnnotationLoading(true);
    try {
      try {
        // Try API call first (when backend supports it)
        await api.deleteAnnotation(conversationState.selectedConversation._id, messageId, annotationId, token);
      } catch (apiError) {
        console.log('API delete not available, updating locally:', apiError.message);
      }
      
      // Update locally regardless (for immediate UI feedback)
      conversationState.setSelectedConversation(prevConvo => {
        if (!prevConvo || !prevConvo.messages) return prevConvo;
        
        const updatedMessages = prevConvo.messages.map(msg => {
          if (msg._id === messageId && msg.annotations) {
            const updatedAnnotations = msg.annotations.filter(ann => ann._id !== annotationId);
            return { ...msg, annotations: updatedAnnotations };
          }
          return msg;
        });
        
        return { ...prevConvo, messages: updatedMessages };
      });
      
      return 'Annotation deleted successfully';
    } catch (err) {
      console.error('Error deleting annotation:', err);
      setAnnotationError(err.message || 'Failed to delete annotation');
      throw err;
    } finally {
      setAnnotationLoading(false);
    }
  }, [conversationState, token]);

  return {
    // State
    annotatingMessageId,
    annotationInput,
    annotationTags,
    annotationError,
    annotationLoading,
    
    // Setters
    setAnnotationInput,
    setAnnotationTags,
    
    // Handlers
    handleStartAnnotation,
    handleCancelAnnotation,
    handleSaveAnnotation,
    handleEditAnnotation,
    handleDeleteAnnotation
  };
};

export default useAnnotationManagement;